import { useCallback, useMemo, useState } from 'react';
import { Alert, Badge, Button, Group, Loader, Modal, ScrollArea, Select, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { normalizeArticle } from '@parts/shared';
import { errorText } from '../api';
import { useI18n } from '../i18n';
import { IconUpload } from '../icons';
import { Workbook, columnName, type SheetData, type SheetRef } from './xlsx';
import classes from './ExcelImportDialog.module.css';

interface Props {
  opened: boolean;
  onClose: () => void;
  knownArticles: Record<string, true>;
  /** Разобранные строки переносятся в таблицу экрана. */
  onApply: (rows: Record<string, string>[]) => void;
}

/** Столбцы, которые фабрика заполняет в выгруженном файле. */
const FIELDS = [
  { key: 'stock', title: 'Наличие у фабрики, шт', match: /налич|stock|qty|avail/i },
  { key: 'price', title: 'Цена фабрики, CNY', match: /цена|price|cost/i },
  { key: 'hs', title: 'HS code', match: /hs/i },
  { key: 'suggestion', title: 'OEM от закупки', match: /замен|аналог|suggest|оем от|oem от/i }
] as const;

interface Source {
  col: number;
  header: string;
  sample: string;
}

/**
 * Загрузка ответа фабрики: файл, выгруженный кнопкой «Выгрузить для фабрики» и заполненный на той стороне.
 * Строки сопоставляются по OEM, значения попадают в таблицу экрана «Закупка».
 */
export function StockImportDialog({ opened, onClose, knownArticles, onApply }: Props) {
  const { lang, t, tx } = useI18n();
  const [book, setBook] = useState<Workbook | null>(null);
  const [sheets, setSheets] = useState<SheetRef[]>([]);
  const [sheetName, setSheetName] = useState('');
  const [data, setData] = useState<SheetData | null>(null);
  const [fileName, setFileName] = useState('');
  const [headerRow, setHeaderRow] = useState(1);
  const [map, setMap] = useState<Record<string, number>>({});
  const [artCol, setArtCol] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    book?.release();
    setBook(null);
    setData(null);
    setFileName('');
    setError(null);
    onClose();
  };

  const sources = useMemo<Source[]>(() => {
    if (!data) return [];
    const cols = new Map<number, Source>();
    for (const [row, line] of data.cells) {
      for (const [col, value] of line) {
        const s = cols.get(col) ?? { col, header: '', sample: '' };
        if (row === headerRow) s.header = value.trim();
        else if (!s.sample) s.sample = value.trim();
        cols.set(col, s);
      }
    }
    return [...cols.values()].sort((a, b) => a.col - b.col);
  }, [data, headerRow]);

  const autoMap = useCallback(
    (parsed: SheetData, head: number) => {
      const cols = new Map<number, string>();
      for (const [row, line] of parsed.cells) if (row === head) for (const [col, v] of line) cols.set(col, v.trim());
      let art = 0;
      let best = 0;
      for (const [col] of cols) {
        let score = 0;
        for (const [row, line] of parsed.cells) {
          if (row === head) continue;
          const v = line.get(col);
          if (v && knownArticles[normalizeArticle(v)]) score++;
        }
        if (score > best) {
          best = score;
          art = col;
        }
      }
      if (!art) for (const [col, h] of cols) if (/oem|артикул|part/i.test(h)) art = col;
      setArtCol(art);
      const next: Record<string, number> = {};
      for (const f of FIELDS) {
        for (const [col, h] of cols) {
          if (col !== art && h && f.match.test(h) && !Object.values(next).includes(col)) {
            next[f.key] = col;
            break;
          }
        }
      }
      setMap(next);
    },
    [knownArticles]
  );

  const openSheet = useCallback(
    (wb: Workbook, sheet: SheetRef) => {
      const parsed = wb.readSheet(sheet);
      setData(parsed);
      setSheetName(sheet.name);
      const first = [...parsed.cells.keys()].sort((a, b) => a - b)[0] ?? 1;
      setHeaderRow(first);
      autoMap(parsed, first);
    },
    [autoMap]
  );

  const onFile = async (file: File | null) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const wb = await Workbook.open(file);
      setBook(wb);
      setFileName(file.name);
      setSheets(wb.sheets);
      const first = wb.sheets[0];
      if (first) openSheet(wb, first);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const rows = useMemo(() => {
    if (!data || !artCol) return [];
    const out: Record<string, string>[] = [];
    for (const r of [...data.cells.keys()].sort((a, b) => a - b)) {
      if (r <= headerRow) continue;
      const art = normalizeArticle(Workbook.valueAt(data, r, artCol));
      if (!art) continue;
      const row: Record<string, string> = { art };
      for (const f of FIELDS) {
        const col = map[f.key];
        if (col) row[f.key] = Workbook.valueAt(data, r, col).trim();
      }
      if (Object.keys(row).length > 1) out.push(row);
    }
    return out;
  }, [data, artCol, map, headerRow]);

  const unknown = rows.filter((r) => !knownArticles[r.art!]).length;

  const colOptions = useMemo(
    () => [
      { value: '0', label: t('не переносить') },
      ...sources.map((s) => ({ value: String(s.col), label: `${columnName(s.col)}${s.header ? ` · ${s.header.slice(0, 30)}` : ''}` }))
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sources, lang]
  );

  return (
    <Modal opened={opened} onClose={close} title={t('Загрузка ответа фабрики')} size={820} padding="lg">
      <Stack gap="md">
        <Alert variant="light" color="accent" py="xs">
          {t('Загрузите файл, который вернула фабрика. Строки сопоставляются по OEM, значения попадут в таблицу — провести их нужно будет как обычно, кнопкой «Проверить и провести».')}
        </Alert>

        <Group gap="md" align="flex-end" wrap="wrap">
          <label className={classes.fileBtn}>
            <IconUpload size={15} />
            {t('Выбрать файл .xlsx')}
            <input type="file" accept=".xlsx,.xlsm" hidden onChange={(e) => void onFile(e.target.files?.[0] ?? null)} />
          </label>
          {fileName && (
            <Text fw={600} className={classes.fileName} title={fileName}>
              {fileName}
            </Text>
          )}
          {data && (
            <>
              <Select
                label={t('Лист')}
                data={sheets.map((s) => ({ value: s.name, label: s.name }))}
                value={sheetName}
                onChange={(v) => {
                  const sheet = sheets.find((s) => s.name === v);
                  if (sheet && book) openSheet(book, sheet);
                }}
                w={180}
                size="sm"
              />
              <Select
                label={t('Строка заголовков')}
                data={[...data.cells.keys()].sort((a, b) => a - b).slice(0, 20).map((r) => ({ value: String(r), label: t('Строка {n}', { n: r }) }))}
                value={String(headerRow)}
                onChange={(v) => {
                  setHeaderRow(Number(v));
                  if (data) autoMap(data, Number(v));
                }}
                w={160}
                size="sm"
              />
            </>
          )}
        </Group>

        {error && (
          <Alert color="red" title={t('Не удалось прочитать файл')}>
            {tx(error)}
          </Alert>
        )}
        {busy && (
          <Group gap="sm">
            <Loader size="sm" />
            <Text c="dimmed">{t('Обрабатываю…')}</Text>
          </Group>
        )}

        {data && (
          <>
            <Group gap="md" wrap="wrap">
              <Select label={t('Столбец с OEM')} data={colOptions} value={String(artCol)} onChange={(v) => setArtCol(Number(v))} w={240} searchable />
              {FIELDS.map((f) => (
                <Select
                  key={f.key}
                  label={tx(f.title)}
                  data={colOptions}
                  value={String(map[f.key] ?? 0)}
                  onChange={(v) => setMap((prev) => ({ ...prev, [f.key]: Number(v) }))}
                  w={200}
                  size="sm"
                  searchable
                />
              ))}
            </Group>

            <Group gap="sm">
              <Badge color="go" variant="light" size="lg" radius="sm">
                {t('Строк: {n}', { n: rows.length })}
              </Badge>
              {unknown > 0 && (
                <Badge color="yellow" variant="light" size="lg" radius="sm">
                  {t('Нет в запросах: {n}', { n: unknown })}
                </Badge>
              )}
            </Group>

            <ScrollArea.Autosize mah={300} type="auto" className={classes.preview}>
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th>OEM</th>
                    {FIELDS.map((f) => (
                      <th key={f.key}>{tx(f.title)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 300).map((r, i) => (
                    <tr key={i} data-state={knownArticles[r.art!] ? 'ok' : 'unknown'}>
                      <td>{r.art}</td>
                      {FIELDS.map((f) => (
                        <td key={f.key}>{r[f.key] ?? ''}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollArea.Autosize>

            <Group justify="flex-end">
              <Button variant="subtle" onClick={close}>
                {t('Отмена')}
              </Button>
              <Button
                disabled={!rows.length}
                onClick={() => {
                  onApply(rows);
                  notifications.show({ color: 'green', title: t('Данные перенесены'), message: t('Строк: {n}', { n: rows.length }) });
                  close();
                }}
              >
                {t('Перенести в таблицу ({n})', { n: rows.length })}
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
}
